import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Provider,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ModalService } from 'mbs-ui-kit';
import { TreeInModalComponent } from 'mbs-ui-shared@components/tree-in-modal/tree-in-modal.component';
import { networkValidPath } from 'mbs-ui-shared@constants';
import { linuxPathValidator, networkPathValidator, winPathValidator } from 'mbs-ui-shared@utils/validators';
import { noop } from 'rxjs';
import { BaseForStepsHelper } from 'wizards/helpers/bases/base-for-steps-helper';
import { DestinationFolderData, DestinationFolderParams } from 'wizards/models/destination-models';
import { ParamsForTreeData } from 'wizards/services/wizard-steps.service';

interface DestinationFolderForm {
  destinationFolder: FormControl<string>;
  destinationNetworkShare?: FormControl<string>;
}

const DestinationFolderValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => DestinationFolderComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-destination-folder',
  templateUrl: './destination-folder.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestinationFolderValueAccessor]
})
export class DestinationFolderComponent implements OnInit, OnChanges {
  @Input() params: DestinationFolderParams;
  @Input() hideLabel = false;
  @Input() disabled = false;
  @Input() forceValidate = false;
  @Input() idPrefix = 'main';
  @Input() destinationLabel = this.i18nPipe.transform('wizards:destFolderLabel');
  @Input() destinationNetworkLabel = this.i18nPipe.transform('wizards:destNetworkFolderLabel');

  public readonly elementsSelector = {
    name: { offlineOrUnix: 'offlineOrUnix', winAndOnline: 'winAndOnline' }
  };

  public needHide = (item: unknown & { type: number }): boolean => +item.type === 4;

  onChange: (value: unknown) => void;
  onTouched: () => void;

  public toFolder = true;

  public mainForm: FormGroup<DestinationFolderForm> = new FormGroup<DestinationFolderForm>({
    destinationFolder: new FormControl('', [this.destinationFolderValidator.bind(this)]),
    destinationNetworkShare: new FormControl('', [this.destinationNetworkShareValidator.bind(this)])
  });

  private innerValue: DestinationFolderData = { destinationFolder: '', valid: false };
  set value(value: DestinationFolderData) {
    this.innerValue = value;
    this.notifyValueChange();
  }
  get value(): DestinationFolderData {
    return this.innerValue;
  }

  constructor(private cdr: ChangeDetectorRef, public i18nPipe: I18NextPipe, private modalService: ModalService) {}

  destinationFolderValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.toFolder && !this.params?.dataForPath.isUnix && !this.params?.isOffline) return null;

    const isRequired = Validators.required(control);

    if (isRequired) return isRequired;

    return this.params?.dataForPath.isUnix ? linuxPathValidator(control) : winPathValidator(control);
  }

  destinationNetworkShareValidator(control: AbstractControl): ValidationErrors | null {
    if (this.toFolder || this.params?.dataForPath.isUnix || this.params?.isOffline) return null;

    const isRequired = Validators.required(control);

    return isRequired ? isRequired : networkPathValidator(control);
  }

  ngOnInit(): void {
    this.mainForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (v) => {
        this.value = { destinationFolder: this.toFolder ? v.destinationFolder : v.destinationNetworkShare, valid: this.mainForm.valid };
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.mainForm) return;

    if (changes.forceValidate?.currentValue && !this.disabled) {
      const control: FormControl<string> = this.mainForm.get(
        this.toFolder ? 'destinationFolder' : 'destinationNetworkShare'
      ) as FormControl<string>;

      if (control.touched !== changes.forceValidate.currentValue) {
        this.forceValidStateControl(control);
      }
    }

    if (!changes?.disabled) return;

    if (changes.disabled.currentValue) {
      this.mainForm.get('destinationFolder').disable();
      return this.mainForm.get('destinationNetworkShare').disable();
    }

    this.toFolderChangeHandler();
  }

  updateForm(value: DestinationFolderData): void {
    if (!value) return;

    this.toFolder = !value.destinationFolder || !networkValidPath.test(value.destinationFolder);

    if (this.toFolder) {
      this.mainForm.get('destinationFolder').reset(value.destinationFolder);
      this.mainForm.get('destinationNetworkShare').reset('');
    } else {
      this.mainForm.get('destinationFolder').reset('');
      this.mainForm.get('destinationNetworkShare').reset(value.destinationFolder);
    }

    if (!this.disabled) this.toFolderChangeHandler();
  }

  toFolderChangeHandler(): void {
    const folder: FormControl<string> = this.mainForm.get('destinationFolder') as FormControl<string>;
    const share: FormControl<string> = this.mainForm.get('destinationNetworkShare') as FormControl<string>;

    folder.updateValueAndValidity();
    share.updateValueAndValidity();

    if (this.toFolder) {
      folder.enable();
      if (share.touched) this.forceValidStateControl(folder);
      return share.disable();
    }

    folder.disable();
    share.enable();
    if (folder.touched) this.forceValidStateControl(share);
  }

  includeExcludeInputHandler(event: Event, name = 'destinationFolder'): void {
    const formattedObj = BaseForStepsHelper.getStringPathFormatted(event, this.params?.dataForPath);

    if (formattedObj?.needUpdate) this.mainForm.get(name).patchValue(formattedObj.path);
  }

  addNetworkShare(): void {
    const path = this.mainForm.get('destinationNetworkShare').value;

    const dataForTree = {
      title: this.i18nPipe.transform('wizards:addNetworkShare', { format: 'title' }),
      hid: this.params?.hid,
      resultFolders: path ? [path] : [],
      isEncrypted: false,
      hideFileType: true,
      isNetwork: true,
      oldBackupContentVariant: true,
      onlyOneSelect: true,
      params: this.getNewNetworkShareTreeParams(),
      dataForPath: this.params?.dataForPath,
      needHide: this.needHide
    };

    this.modalService
      .openCustom(TreeInModalComponent, { data: dataForTree, collapsing: true })
      .then((result: string[]) => result?.length && this.mainForm.get('destinationNetworkShare').setValue(result[0]))
      .catch(noop);
  }

  destinationFolderTreeOpen(): void {
    const folder = this.mainForm.get('destinationFolder').value;
    const dataForTree = {
      title: this.i18nPipe.transform('wizards:dest_tree_in_modal_title', { format: 'title' }),
      hid: this.params?.hid,
      resultFolders: folder ? [folder] : [],
      isEncrypted: false,
      hideFileType: true,
      oldBackupContentVariant: true,
      onlyOneSelect: true,
      params: this.getNewTreeParams(),
      dataForPath: this.params?.dataForPath
    };

    this.modalService
      .openCustom(TreeInModalComponent, { data: dataForTree, collapsing: true })
      .then((result: string[]) => {
        if (result?.length) this.mainForm.get('destinationFolder').setValue(result[0]);
      })
      .catch(noop);
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
      this.cdr.markForCheck();
    }
  }

  writeValue(value: DestinationFolderData): void {
    this.innerValue = value;
    this.updateForm(value);
  }

  registerOnChange(fn: (data: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private getNewTreeParams(): ParamsForTreeData {
    return {
      agentType: 'Backup',
      commandType: 'getComputerContent',
      params: { path: '', offset: 0, limit: 300, isAsync: true, order: 'DisplayNameAsc' }
    };
  }

  private getNewNetworkShareTreeParams(): ParamsForTreeData {
    return {
      agentType: 'Backup',
      commandType: 'getComputerContent',
      params: { path: '\\\\', offset: 0, limit: 300, isAsync: true, order: 'DisplayNameAsc' }
    };
  }

  private forceValidStateControl(control: FormControl<string>): void {
    control.markAsTouched();
    control.markAsDirty();
    control.updateValueAndValidity();
  }
}
